import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const HealthTracking = () => {
  const { user } = useUnifiedAuth();
  const [healthEntries, setHealthEntries] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    bmi: '',
    age: '',
    gender: '',
    blood_type: '',
    glasses_prescription: '',
    cholesterol_level: '',
    general_health_status: '',
    weight: '',
    height: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    notes: ''
  });

  useEffect(() => {
    if (user?.user_type === 'patient' || user?.user_type === 'user') {
      fetchHealthData();
    }
  }, [user]);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      const [entriesResponse, dashboardResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health/entries?limit=10`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          },
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health/dashboard`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          },
        })
      ]);

      if (entriesResponse.ok) {
        const entriesData = await entriesResponse.json();
        setHealthEntries(entriesData.entries || []);
      }

      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        setDashboardData(dashboardData);
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty values
    const filteredData = Object.fromEntries(
      Object.entries(formData).filter(([key, value]) => value !== '')
    );

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health/entry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filteredData),
      });

      if (response.ok) {
        setShowAddForm(false);
        setFormData({
          bmi: '',
          age: '',
          gender: '',
          blood_type: '',
          glasses_prescription: '',
          cholesterol_level: '',
          general_health_status: '',
          weight: '',
          height: '',
          blood_pressure_systolic: '',
          blood_pressure_diastolic: '',
          notes: ''
        });
        await fetchHealthData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save health entry');
      }
    } catch (error) {
      console.error('Error saving health entry:', error);
      alert('Failed to save health entry');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getHealthStatusColor = (status) => {
    const colors = {
      'excellent': 'bg-green-100 text-green-800',
      'good': 'bg-blue-100 text-blue-800',
      'fair': 'bg-yellow-100 text-yellow-800',
      'poor': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Don't show for non-patients
  if (user?.user_type !== 'patient' && user?.user_type !== 'user') {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-6 shadow">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Health Tracking</h2>
            <p className="text-gray-600 mt-1">Monitor your health metrics over time</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Entry
          </button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboardData && (
        <div className="bg-white rounded-lg p-6 shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Health Overview</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{dashboardData.total_entries}</div>
              <div className="text-sm text-gray-600">Total Entries</div>
            </div>
            
            {dashboardData.latest_entry && dashboardData.latest_entry.bmi && (
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{dashboardData.latest_entry.bmi}</div>
                <div className="text-sm text-gray-600">Latest BMI</div>
              </div>
            )}
            
            {dashboardData.latest_entry && dashboardData.latest_entry.cholesterol_level && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{dashboardData.latest_entry.cholesterol_level}</div>
                <div className="text-sm text-gray-600">Cholesterol (mg/dL)</div>
              </div>
            )}
            
            {dashboardData.latest_entry && dashboardData.latest_entry.general_health_status && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-purple-600 capitalize">
                  {dashboardData.latest_entry.general_health_status}
                </div>
                <div className="text-sm text-gray-600">Health Status</div>
              </div>
            )}
          </div>

          {/* Health Status Distribution (Pie Chart Placeholder) */}
          {dashboardData.health_trends?.health_status_distribution && Object.keys(dashboardData.health_trends.health_status_distribution).length > 0 && (
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Health Status Distribution</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(dashboardData.health_trends.health_status_distribution).map(([status, count]) => (
                  <div key={status} className={`p-3 rounded-lg ${getHealthStatusColor(status)}`}>
                    <div className="font-bold">{count}</div>
                    <div className="text-sm capitalize">{status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Entries */}
      <div className="bg-white rounded-lg p-6 shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Health Entries</h3>
        
        {healthEntries.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4">📊</div>
            <p className="text-gray-500">No health entries yet</p>
            <p className="text-gray-400 text-sm mt-1">Add your first entry to start tracking your health</p>
          </div>
        ) : (
          <div className="space-y-4">
            {healthEntries.map((entry) => (
              <div key={entry.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">
                    {formatDate(entry.entry_date)}
                  </div>
                  {entry.general_health_status && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(entry.general_health_status)}`}>
                      {entry.general_health_status}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
                  {entry.bmi && (
                    <div>
                      <div className="text-gray-500">BMI</div>
                      <div className="font-medium">{entry.bmi}</div>
                    </div>
                  )}
                  {entry.age && (
                    <div>
                      <div className="text-gray-500">Age</div>
                      <div className="font-medium">{entry.age}</div>
                    </div>
                  )}
                  {entry.gender && (
                    <div>
                      <div className="text-gray-500">Gender</div>
                      <div className="font-medium capitalize">{entry.gender}</div>
                    </div>
                  )}
                  {entry.blood_type && (
                    <div>
                      <div className="text-gray-500">Blood Type</div>
                      <div className="font-medium">{entry.blood_type}</div>
                    </div>
                  )}
                  {entry.cholesterol_level && (
                    <div>
                      <div className="text-gray-500">Cholesterol</div>
                      <div className="font-medium">{entry.cholesterol_level} mg/dL</div>
                    </div>
                  )}
                </div>
                
                {entry.notes && (
                  <div className="mt-3 text-sm text-gray-600">
                    <strong>Notes:</strong> {entry.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Entry Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowAddForm(false)}></div>
            
            <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Add Health Entry</h3>
                </div>
                
                <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">BMI</label>
                      <input
                        type="number"
                        step="0.1"
                        name="bmi"
                        value={formData.bmi}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 22.5"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 30"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Blood Type</label>
                      <select
                        name="blood_type"
                        value={formData.blood_type}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Blood Type</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Glasses Prescription</label>
                      <input
                        type="text"
                        name="glasses_prescription"
                        value={formData.glasses_prescription}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., -2.5, +1.25"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cholesterol Level (mg/dL)</label>
                      <input
                        type="number"
                        name="cholesterol_level"
                        value={formData.cholesterol_level}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 180"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">General Health Status</label>
                      <select
                        name="general_health_status"
                        value={formData.general_health_status}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Health Status</option>
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="poor">Poor</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 70.5"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Height (cm)</label>
                      <input
                        type="number"
                        name="height"
                        value={formData.height}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 175"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Blood Pressure (Systolic)</label>
                      <input
                        type="number"
                        name="blood_pressure_systolic"
                        value={formData.blood_pressure_systolic}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 120"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Blood Pressure (Diastolic)</label>
                      <input
                        type="number"
                        name="blood_pressure_diastolic"
                        value={formData.blood_pressure_diastolic}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 80"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Any additional notes about your health..."
                    ></textarea>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Entry
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthTracking;